// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { initActionTypes, type ReduxNext, featureToggleSelectors, dealerSelectors } from '@makemydeal/dr-shared-store';
import { offerSessionUtils, mapVehicleInsuranceForSync } from '@makemydeal/dr-shared-ui-utils';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { scopedActionsCreators, scopedStateSelectors, scopedActions } from '@makemydeal/shared-scoped-store';
import * as dealTemplateSelectors from '../selectors/dealTemplateSelectors';
import * as offerInfoSelectors from '../selectors/offerInfoSelectors';
import * as editCustomerSlideoutSelectors from '../selectors/editCustomerSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';
import * as baseDealSelectors from '../selectors/BaseDealSelectors';

// consts/enums
import { INTERNAL_UPDATE_PREV_OFFER_AFTER_INIT } from '../actionTypes/internalPrevOfferActionTypes';
import {
    MAKE_CURRENT_COMPLETED,
    OFFER_SAVE_FAILURE,
    OFFER_SAVE_NOCHANGE_REQUEST,
    OFFER_SAVE_SUCCESS,
    SAVE_OFFER,
    SAVE_OFFER_AFTER_CANCEL,
    SEND_TO_DEALER_TRACK
} from '../actionTypes/offerActionTypes';
import { DASH_MENU_LIFECYCLE_INIT_FINISH } from '../actionTypes/vehicleProtectionActionTypes';

// actions
import { offerSaveNoChangeSuccess, saveOfferRSAA } from '../actions/offerActionCreators';
import { thunkEcommerceCancellationWithSave } from '../actions/orderActionCreators';

// utils
import { BuildPayloadPurpose } from '@makemydeal/dr-dash-bff-types';
import { setLockPaymentInputsInSV } from '../actions/dealLockingActionCreators';
import * as multiScenarioActionCreators from '../actions//multiScenarioActionCreators';
import * as multiScenarioSelectors from '../selectors/multiScenarioSelectors';
import type { UpdateInternalPrevOfferAfterInitAction } from '../actions/internalPrevOfferActionCreators';
import {
    internalPatchPrevOfferPayloadAftermarketItems,
    internalUpdatePrevOfferFullPayload
} from '../actions/internalPrevOfferActionCreators';
import {
    SCENARIOS_FETCH_FAILURE,
    SCENARIOS_FETCH_REQUEST,
    SCENARIOS_FETCH_SUCCESS,
    TEMPLATE_SCENARIO_FETCH_FAILURE,
    TEMPLATE_SCENARIO_FETCH_REQUEST,
    TEMPLATE_SCENARIO_FETCH_SUCCESS
} from '../actionTypes/multiScenarioActionTypes';
import { SCENARIO_HYDRATE_RECEIVED } from '../actionTypes/asyncScenarioLoadActionTypes';
import * as offerSelectors from '../selectors/offerRedux';
import { getSalesManager } from '../selectors/employeeDetailsSelectors';
import { getBridgeId } from '../selectors/bridgeUserSelectors';
import { willStampLoggedInUserAsSalesManager } from '../utils/employeeUtils';
import { buildOfferSavePayloadObject } from '../utils/offerPayloadUtil';
import { getPurpose } from '../utils/saveOfferStateUtils';
import { isTemplateScenarioPayload, resolveTemplateScenarioScopeId, validateScenario } from '../utils/multiScenarioUtils';
import { logTaxRatesOnOpen } from '../utils/taxRateOnOpenTelemetry';
import { syncCustomerRSAA } from '../actions/editCustomerActionCreators';
import { type IShopper, type CoBuyer, ApplicationCodes } from '@makemydeal/dr-platform-types';
import { BOOTSTRAP_INITIAL_LOAD } from '@makemydeal/dr-shared-store/actionTypes/bootstrapActionTypes';

const { INIT_PENCIL_SUCCESS, INIT_SUCCESS, INIT_INVALID_DEAL_SUCCESS, INIT_PENCIL_INVALID_DEAL } = initActionTypes;

const logSalesManagerStamp = (state: any, action: AnyFSA) => {
    if (!willStampLoggedInUserAsSalesManager(state)) {
        return;
    }
    logNewRelicPageAction('MV:Save:WriteSalesManager', {
        dealXgId: offerSelectors.getDealXgId(state),
        isDraft: Boolean(action.meta?.isDraft),
        bridgeId: getBridgeId(state),
        hadExistingSalesManager: Boolean(getSalesManager(state)),
        dontClobberToggleEnabled: Boolean(featureToggleSelectors.isDontClobberSalesManagerOnSaveEnabled(state))
    });
};

// TODO: Revamp the `store` type to more accurately reflect the multi-scenario work
export const middleware = (store: any) => (next: ReduxNext) => (action: AnyFSA) => {
    next(action);

    const state = store.getState();

    switch (action.type) {
        case BOOTSTRAP_INITIAL_LOAD: {
            logNewRelicPageAction('GLOBAL_TOGGLES_AVAILABLE', { success: !!window.DealWorkflow?.toggles });
            break;
        }
        // just for potential backwards compatibility with the other activities from SP
        case INIT_PENCIL_INVALID_DEAL:
        case INIT_PENCIL_SUCCESS: {
            const selectedOffer = action.payload.offer || {};
            const { commonConsumerId: commonConsumerIdFromOffer, commonOrgId: commonOrgIdFromOffer } = selectedOffer;
            const isSessionCCIDFallbackDisabled = featureToggleSelectors.disableCCIDSessionFallback(state);
            let commonConsumerIdFromSession;
            if (!isSessionCCIDFallbackDisabled) {
                commonConsumerIdFromSession = offerSessionUtils.getCommonConsumerId();
            }
            const commonConsumerId =
                commonConsumerIdFromOffer || commonConsumerIdFromSession || selectedOffer.shopper?.commonConsumerId;

            const commonOrgId = commonOrgIdFromOffer || '';

            if (commonConsumerId) {
                offerSessionUtils.setCommonConsumerId(commonConsumerId);
            }

            action.payload.offer = { ...action.payload.offer, commonOrgId, commonConsumerId };
            if (action.type === INIT_PENCIL_SUCCESS) {
                store.dispatch({
                    type: INIT_SUCCESS,
                    payload: action.payload,
                    meta: { ...action.meta, sourceApplication: ApplicationCodes.MV }
                });
                logTaxRatesOnOpen(store.getState());
            } else {
                store.dispatch({ type: INIT_INVALID_DEAL_SUCCESS, payload: action.payload });
            }

            break;
        }
        case INTERNAL_UPDATE_PREV_OFFER_AFTER_INIT: {
            const actionTyped = action as UpdateInternalPrevOfferAfterInitAction;
            const offerSavePayload = buildOfferSavePayloadObject(state);
            const dealXgId = actionTyped.payload?.dealXgId || '';
            const dealXgVersion = offerSavePayload?.offer?.dealExchangeVersionId || '';

            store.dispatch(internalUpdatePrevOfferFullPayload(dealXgId, dealXgVersion, offerSavePayload));
            break;
        }
        case DASH_MENU_LIFECYCLE_INIT_FINISH: {
            const offerSavePayload = buildOfferSavePayloadObject(state, BuildPayloadPurpose.MenuInit);
            const dealXgId = offerSelectors.getDealXgId(state);
            if (dealXgId) {
                store.dispatch(
                    internalPatchPrevOfferPayloadAftermarketItems(
                        offerSavePayload?.menu?.menuProducts,
                        offerSavePayload?.accessories
                    )
                );
            }
            break;
        }
        case SAVE_OFFER: {
            const purpose = getPurpose(action);
            const state = store.getState();

            // Check if ecommerce order cancellation is needed
            const isEcommercePendingCancellation = offerInfoSelectors.getEcommercePendingCancellationStatus(state);

            // If ecommerce order cancellation is needed, dispatch the thunk action to handle the cancellation and save flow
            if (isEcommercePendingCancellation) {
                store.dispatch(thunkEcommerceCancellationWithSave({ purpose }, action.meta));
            } else {
                // Proceed with normal save flow
                logSalesManagerStamp(state, action);
                const { isDraft } = action.meta || {};
                const offerSavePayload = buildOfferSavePayloadObject(state, purpose);
                const saveOfferAction: any = saveOfferRSAA({
                    state,
                    source: action.meta?.source,
                    offerSavePayload,
                    isDraft,
                    originalAction: action.meta?.originalAction,
                    templateId: action.meta?.templateId,
                    saveStartTime: action.meta?.saveStartTime
                });
                store.dispatch(saveOfferAction);

                // Sync customer data if there are pending changes
                syncCustomerToCommonConsumer(state, store);
            }
            break;
        }

        case SAVE_OFFER_AFTER_CANCEL: {
            // Resume save after successful cancellation
            const { isDraft } = action.meta || {};
            const state = store.getState();
            const purpose = getPurpose(action);

            logSalesManagerStamp(state, action);

            const offerSavePayload = buildOfferSavePayloadObject(state, purpose);

            const saveOfferAction: any = saveOfferRSAA({
                state,
                source: action.meta?.source,
                offerSavePayload,
                isDraft,
                originalAction: action.meta?.originalAction,
                templateId: action.meta?.templateId,
                clearDealRefId: true
            });
            store.dispatch(saveOfferAction);

            // Sync customer data if there are pending changes
            syncCustomerToCommonConsumer(state, store);

            break;
        }

        case SEND_TO_DEALER_TRACK: {
            const purpose = BuildPayloadPurpose.SendToDealerTrack;
            const offerSavePayload = buildOfferSavePayloadObject(state, purpose);
            const saveOfferAction: any = saveOfferRSAA({ state, offerSavePayload });
            store.dispatch(saveOfferAction);
            break;
        }

        case OFFER_SAVE_NOCHANGE_REQUEST: {
            const dealXgId = action.payload.dealXgId;
            const dealXgVersion = action.payload.dealXgVersion;
            const offerSaveSource = action.meta?.source;
            setTimeout(() => {
                store.dispatch(offerSaveNoChangeSuccess(dealXgId, dealXgVersion, offerSaveSource));
            }, 1000);
            break;
        }

        case SCENARIO_HYDRATE_RECEIVED: {
            const { payload: scenario } = action;
            const order = scopedStateSelectors.getScopesOrder(state);

            const isTemplateScenario = isTemplateScenarioPayload(scenario);
            const scenarioId = isTemplateScenario
                ? resolveTemplateScenarioScopeId(scenario, order, scopedStateSelectors.getPrimaryScopeId(state))
                : validateScenario(scenario, order);
            if (!scenarioId) break;

            const activeComparison = multiScenarioSelectors.getActiveComparison(state);
            const baggageScenario = isTemplateScenario
                ? activeComparison?.scenarios?.[scenario.template.scenarioIndex]
                : activeComparison?.scenarios?.find(({ id }) => id === scenarioId);
            /* istanbul ignore next -- offer is non-null when scenarioId is truthy; optional-chain null branches are logically unreachable here */
            const offerType = scenario?.offer?.type;

            // Scoped INIT_PENCIL_SUCCESS. Strip featureToggles from every scenario hydrated via this path:
            // feature toggles are session/dealer-global, and the global featureToggleReducer merges every
            // INIT_PENCIL_SUCCESS — so hydrating a scenario with its persisted snapshot would clobber the live
            // global toggles (DE749297, e.g. enableAiAltDealStructureRecommendations true->false on multi-scenario reload).
            store.dispatch(
                scopedActions.createScopedAction(scenarioId, {
                    type: INIT_PENCIL_SUCCESS,
                    payload: multiScenarioActionCreators.withPrimaryOfferToggles(
                        multiScenarioActionCreators.withoutScenarioFeatureToggles(scenario),
                        store.getState()
                    )
                })
            );

            // initializeScopes for this one scenario
            store.dispatch(scopedActionsCreators.initializeScopes([scenarioId]));

            // Scoped payment grid actions
            if (baggageScenario) {
                /**
                 * US2085313: the same thunk the other load path uses, rather than `buildPaymentGridActions`
                 * directly.
                 *
                 * That builder only knows how to read the comparison entry's inline `paymentGrid`, and
                 * dash-bff drops it once the compressed item is written — so a scenario arriving here got no
                 * restore, no axes and no toggle, and which load path a dealer happened to take decided
                 * whether their saved grid came back. The thunk owns the resolve/freshness/decompress
                 * sequence and scopes its own actions, exactly as `handleLoadScenarioStartingCash` below does.
                 */
                store.dispatch(multiScenarioActionCreators.handleLoadScenarioPaymentGrid(scenarioId, baggageScenario));

                // Conditional scoped setLockPaymentInputsInSV
                if (baggageScenario.isLocked !== undefined && offerType) {
                    store.dispatch(
                        scopedActions.createScopedAction(scenarioId, setLockPaymentInputsInSV(offerType, baggageScenario.isLocked))
                    );
                }
            }

            // Per frame, not on SCENARIOS_INGEST_COMPLETE — that also fires on the 15s async-load timeout, by
            // which point a seed could overwrite a value the user already typed into an interactive card.
            store.dispatch(multiScenarioActionCreators.handleLoadScenarioStartingCash(scenarioId, baggageScenario));

            // DO NOT dispatch SCENARIOS_INGEST_COMPLETE here — socketMiddleware owns it.
            break;
        }

        case SCENARIOS_FETCH_REQUEST: {
            // V1 sync path: create skeleton scopes for loading indicators
            store.dispatch(multiScenarioActionCreators.addUninitializedScenariosToState());
            // Note: async timeout for V2 is now started in socketMiddleware on BOOTSTRAP_SUCCESS
            break;
        }

        case TEMPLATE_SCENARIO_FETCH_REQUEST: {
            const order = scopedStateSelectors.getInitializedScopesOrder(state);
            const currentScopeId = scopedStateSelectors.getCurrentScopeId(state);

            store.dispatch(
                scopedActionsCreators.createUninitializedScopesFromOrder(
                    order.map((scopeId) => {
                        if (scopeId === currentScopeId) {
                            return action.meta.scenarioId;
                        }

                        return scopeId;
                    })
                )
            );
            store.dispatch(scopedActionsCreators.deleteScope(currentScopeId));

            break;
        }

        case TEMPLATE_SCENARIO_FETCH_SUCCESS: {
            if (action.payload.scenario?.baggage) {
                store.dispatch(multiScenarioActionCreators.updateActiveComparison(action.payload.scenario));
            }

            store.dispatch(multiScenarioActionCreators.addAlternateScenariosToState([action.payload.scenario]));
            break;
        }

        case SCENARIOS_FETCH_SUCCESS: {
            const { scenarios } = action.payload;
            const dealXgVersion = action.meta?.dealXgVersion;
            if (dealXgVersion) {
                break;
            }
            if (offerInfoSelectors.getIsMakingCurrent(state)) {
                store.dispatch({ type: MAKE_CURRENT_COMPLETED });
                store.dispatch(multiScenarioActionCreators.addMakeCurrentAlternateScenariosToState(scenarios));
                break;
            }
            if (dealTemplateSelectors.getIsDealTemplateApplied(state)) {
                store.dispatch(multiScenarioActionCreators.addTemplateScenariosToState(scenarios));
                break;
            }

            // V1 sync path: dispatch chain
            store.dispatch(multiScenarioActionCreators.addAlternateScenariosToState(scenarios));
            break;
        }

        case TEMPLATE_SCENARIO_FETCH_FAILURE:
        case SCENARIOS_FETCH_FAILURE: {
            store.dispatch(multiScenarioActionCreators.removeAlternateScenariosFromState());
            break;
        }

        case OFFER_SAVE_SUCCESS: {
            const dealXgId = action.payload.dealXgId;
            const dealXgVersion = action.payload.dealXgVersion;
            if (dealXgId && !offerSessionUtils.getDealXgId()) {
                offerSessionUtils.setSessionValue('dealXgId', dealXgId);
            }

            // Log save success duration using timestamp from original SAVE_OFFER action
            const saveStartTime = action.meta.saveStartTime;
            if (saveStartTime) {
                const state = store.getState();
                const saveOfferDuration = performance.now() - saveStartTime;
                logNewRelicPageAction('MV:SaveOfferCompleted', {
                    saveOfferDuration,
                    saveResult: 'success',
                    source: action.meta.source,
                    dealXgId,
                    dealXgVersion,
                    dealerId: dealerSelectors.getDealerId(state)
                });
            }
            break;
        }

        case OFFER_SAVE_FAILURE: {
            // Log save failure duration using timestamp from original SAVE_OFFER action
            const saveStartTime = action.meta.saveStartTime;
            if (saveStartTime) {
                const state = store.getState();
                const saveOfferDuration = performance.now() - saveStartTime;
                logNewRelicPageAction('MV:SaveOfferCompleted', {
                    saveOfferDuration,
                    saveResult: 'error',
                    source: action.meta.source,
                    dealXgId: offerSelectors.getDealXgId(state),
                    dealXgVersion: offerSelectors.getDealXgVersion(state),
                    dealerId: dealerSelectors.getDealerId(state)
                });
            }
            break;
        }

        default:
            break;
    }
};

export const syncCustomerToCommonConsumer = (state: any, store: any) => {
    const customerToSyncStatus = editCustomerSlideoutSelectors.getCustomersToSync(state);
    const customersToSync: { customers: Array<Record<string, any>> } = { customers: [] };

    const removeUnwantedFields = (customer: IShopper | CoBuyer) => {
        const {
            contactPreferences: _contactPreferences,
            estimatedCreditScore: _estimatedCreditScore,
            pixallId: _pixallId,
            userConsent: _userConsent,
            birthDate: _birthDate,
            ...customerSyncPayload
        } = customer;
        return customerSyncPayload;
    };
    const buyerInfo = offerReduxSelectors.getShopperInfo(state);
    const coBuyerInfo = offerReduxSelectors.getCoBuyerInfo(state);
    const isSyncCustomerCardFieldsEnabled = featureToggleSelectors.isSyncCustomerCardFieldsEnabled(state);
    const vehicleInsuranceInfo = baseDealSelectors.getVehicleInsuranceInfo(state);

    if (customerToSyncStatus.includes(buyerInfo.commonConsumerId)) {
        customersToSync.customers.push({
            ...removeUnwantedFields(buyerInfo),
            ...(isSyncCustomerCardFieldsEnabled &&
                vehicleInsuranceInfo && { insurance: mapVehicleInsuranceForSync(vehicleInsuranceInfo) })
        });
    }
    if (coBuyerInfo && customerToSyncStatus.includes(coBuyerInfo.commonConsumerId)) {
        customersToSync.customers.push(removeUnwantedFields(coBuyerInfo));
    }

    if (customersToSync.customers.length > 0) {
        store.dispatch(syncCustomerRSAA(customersToSync));
    }
};
